import { ButtonTypes } from './types'
import { Theme } from '@/constants/theme'

export type NoGradientBtnTypes = Exclude<
  ButtonTypes,
  'purpleGradientWithContour' | 'primaryGradient'
>
export type GradientBtnTypes = Extract<
  ButtonTypes,
  'purpleGradientWithContour' | 'primaryGradient'
>

type LinearGradientColors = [string, string, ...string[]]

export const getBtnBG = (
  btnType: NoGradientBtnTypes,
  theme?: Theme,
  disabled?: boolean,
) => {
  if (disabled)
    return theme?.colors.background.buttonPrimaryDisabled || '#5F6163'

  const backgrounds: Record<NoGradientBtnTypes, string> = {
    primaryGreen: theme?.colors.background.buttonPrimaryEnabled || '#0CCF7D',
    contouredSecondaryGray: theme?.colors.background.secondaryGray || '#454647',
    secondaryGray: theme?.colors.background.secondaryGray || '#454647',
    secondaryPurple: theme?.colors.background.secondaryBlue || '#7059FF',
    secondaryGrayDark:
      theme?.colors.background.secondaryGrayOpacity01 ||
      'rgba(255, 255, 255, 0.10)',
    secondaryGrayLight:
      theme?.colors.background.secondaryGrayLight || '#FFFFFF',
    secondaryBlue: theme?.colors.background.secondaryBlue || '#2970FF',
    contouredSecondaryGreen:
      theme?.colors.background.buttonPrimaryEnabled || '#0CCF7D',
    tertiaryWhite: theme?.colors.background.tertiaryWhite || '#FFFFFF',
    blur: theme?.colors.background.tertiaryBlur || 'rgba(255, 255, 255, 0.40)',
    link: 'transparent',
  }

  return (
    backgrounds[btnType] ??
    (theme?.colors.background.buttonPrimaryEnabled || '#0CCF7D')
  )
}

export const getGradientBtnBG = (
  btnType: GradientBtnTypes,
  theme?: Theme,
  disabled?: boolean,
): [string, string, ...string[]] => {
  const defaultGradient: [string, string, ...string[]] = [
    theme?.colors.background.buttonPrimaryGradient1 || '#7866FF',
    theme?.colors.background.buttonPrimaryGradient2 || '#5987FF',
  ]

  if (disabled) {
    const disabledColor =
      theme?.colors.background.buttonPrimaryDisabled || '#5F6163'
    return [disabledColor, disabledColor]
  }

  const backgrounds: Record<GradientBtnTypes, LinearGradientColors> = {
    primaryGradient: defaultGradient,
    purpleGradientWithContour: [
      'rgba(120, 102, 255, 0.20)',
      'rgba(89, 135, 255, 0.20)',
    ],
  }

  return backgrounds[btnType] ?? defaultGradient
}

export const getBtnBorder = (
  btnType: ButtonTypes,
  theme?: Theme,
  disabled?: boolean,
) => {
  const borderWidth = disabled ? 0 : 2

  const borders: Partial<
    Record<ButtonTypes, { borderWidth: number; borderColor: string }>
  > = {
    contouredSecondaryGray: {
      borderWidth,
      borderColor: theme?.colors.background.secondaryGray || '#454647',
    },
    contouredSecondaryGreen: {
      borderWidth,
      borderColor: theme?.colors.background.buttonPrimaryEnabled || '#0CCF7D',
    },
    purpleGradientWithContour: {
      borderWidth,
      borderColor: theme?.colors.background.buttonPrimaryGradient1 || '#7866FF',
    },
  }

  return borders[btnType] ?? { borderWidth: 0, borderColor: undefined }
}

export const getBtnContentColor = (
  btnType: ButtonTypes,
  theme?: Theme,
  disabled?: boolean,
) => {
  const defaultContentColor = {
    iconColor: 'label.buttonPrimaryEnabled',
    color: theme?.colors.label.buttonPrimaryEnabled || '#FFFFFF',
  }

  if (disabled) {
    return {
      iconColor: 'label.buttonPrimaryDisabled',
      color: theme?.colors.label.buttonPrimaryDisabled || '#AAAEB1',
    }
  }

  const contentColors: Record<
    ButtonTypes,
    { iconColor: string; color: string }
  > = {
    primaryGradient: defaultContentColor,
    primaryGreen: defaultContentColor,
    contouredSecondaryGray: {
      iconColor: 'label.buttonContouredGray',
      color: theme?.colors.label.default || '#FFFFFF',
    },
    secondaryGrayLight: {
      iconColor: 'label.buttonSecondaryGrayLightEnabled',
      color: theme?.colors.background.secondaryGray || '#454647',
    },
    tertiaryWhite: {
      iconColor: 'label.tertiaryWhite',
      color: theme?.colors.background.secondaryGray || '#454647',
    },
    secondaryGray: defaultContentColor,
    secondaryGrayDark: defaultContentColor,
    secondaryBlue: defaultContentColor,
    secondaryPurple: defaultContentColor,
    contouredSecondaryGreen: defaultContentColor,
    purpleGradientWithContour: defaultContentColor,
    blur: defaultContentColor,
    link: {
      iconColor: 'label.buttonSecondaryBlue',
      color: theme?.colors.label.buttonSecondaryBlue || '#2970FF',
    },
  }

  return contentColors[btnType] ?? contentColors.primaryGradient
}
